/**
 * Utility functions for handling canonical URLs
 */

/**
 * Get the canonical URL for the current page
 * @param {string} path - The current path (e.g., '/about', '/contact-us')
 * @param {boolean} useWww - Whether to use www subdomain (default: true)
 * @returns {string} The canonical URL
 */
export const getCanonicalUrl = (path = '', useWww = true) => {
    const baseUrl = useWww ? 'https://www.dev-hire.com' : 'https://dev-hire.com';
    
    // Remove leading slash if present and ensure proper formatting
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    
    // For home page, don't add trailing slash
    if (cleanPath === '/') {
        return baseUrl;
    }
    
    return `${baseUrl}${cleanPath}`;
};

/**
 * Get the current page path from window.location
 * @returns {string} The current path
 */
export const getCurrentPath = () => {
    if (typeof window !== 'undefined') {
        return window.location.pathname;
    }
    return '/';
};

/**
 * Check if the current URL is the www version
 * @returns {boolean} True if current URL has www subdomain
 */
export const isWwwVersion = () => {
    if (typeof window !== 'undefined') {
        return window.location.hostname.startsWith('www.');
    }
    return false;
};

/**
 * Get the appropriate canonical URL based on current page and domain
 * @param {string} customPath - Optional custom path (defaults to current path)
 * @returns {string} The canonical URL
 */
export const getAppropriateCanonicalUrl = (customPath = null) => {
    const path = customPath || getCurrentPath();
    const useWww = isWwwVersion();
    return getCanonicalUrl(path, useWww);
};

/**
 * Get the base URL for structured data (JSON-LD)
 * Always use www version for consistency in structured data
 * @returns {string} The base URL
 */
export const getStructuredDataBaseUrl = () => {
    return 'https://www.dev-hire.com';
};

/**
 * Get the logo URL for structured data
 * @returns {string} The logo URL
 */
export const getStructuredDataLogoUrl = () => {
    return `${getStructuredDataBaseUrl()}/images/logo.png`;
};
